
import { formatDate, formatByCountry } from "@/app/infrastructures/misc/Utils";
import Icons from "@/app/ui/components/icons/index.vue";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import {
  ListSummaryItem,
  RequestListSummary,
  SummaryDataList,
} from "@/domain/entities/PickupDashboardSummary";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import TableSummary from "../../components/table-summary/index.vue";

interface ImageItem {
  image: string;
  url: string;
}

export default defineComponent({
  name: "PickupDashboardMonitoringCRRPIC",

  components: {
    TableSummary,
    Icons,
  },

  setup() {
    const listSummaryData = ref<SummaryDataList>(new SummaryDataList());

    const page = ref(1);
    const limit = ref(20);

    const image = ref<string>("");
    const images = ref<ImageItem[]>([]);
    const showImagePreview = ref<boolean>(false);

    const sortKey = ref<string | null>("status_at");
    const sortOrder = ref<"asc" | "desc" | null>("desc");

    const router = useRouter();

    const columns = [
      {
        key: "no",
        label: "pickupDashboard.column.no",
        style: { width: "60px", textAlign: "center" }
      },
      {
        key: "shipmentId",
        label: "pickupDashboard.column.shipmentId",
        style: { width: "180px" }
      },
      {
        key: "courierName",
        label: "pickupDashboard.column.courierName",
        sortable: true
      },
      {
        key: "senderName",
        label: "pickupDashboard.column.senderName",
        sortable: true
      },
      {
        key: "shipmentType",
        label: "pickupDashboard.column.shipmentType"
      },
      {
        key: "totalQuantity",
        label: "pickupDashboard.column.totalKoli",
        style: { width: "80px", textAlign: "center" },
        sortable: true
      },
      {
        key: "statusAt",
        label: "pickupDashboard.column.pickUpDate",
        style: { width: "230px" }
      },
      {
        key: "proofs",
        label: "pickupDashboard.column.pickUpProof"
      },
      {
        key: "actionBooking",
        label: "pickupDashboard.column.booking",
        style: { width: "80px", textAlign: "center" }
      }
    ];

    const waNumber = (phone: string) => String(phone || "").replace(/\D/g, "");

    const detailSummary = computed(
      () => PickupDashboardController.detailSummary
    );

    const pagination = computed(() => PickupDashboardController.pagination);

    const startIndex = computed(
      () => (pagination.value.page - 1) * pagination.value.limit
    );

    const isLoading = computed(
      () => PickupDashboardController.loadingDetailSummary
    );

    const camelToSnake = (value: string): string => {
      return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    const fetchListSummary = async () => {
      PickupDashboardController.setFilter({
        status: "CRRPIC",
      });

      const filterResult = PickupDashboardController.currentFilter;
      const startDate = filterResult.startDate || "";
      const endDate = filterResult.endDate || "";

      await PickupDashboardController.getListSummary(
        new RequestListSummary({
          q: filterResult.q,
          startDate: startDate,
          endDate: endDate,
          status: filterResult.status,
          shipmentType: filterResult.shipmentType,
          sortBy: sortOrder.value?.toUpperCase() ?? "",
          orderBy: camelToSnake(sortKey.value ?? filterResult.orderBy),
          page: page.value,
          limit: limit.value,
        })
      );
    };

    const handleSort = async ({
      key,
      order,
    }: {
      key: string;
      order: "asc" | "desc";
    }) => {
      sortKey.value = key;
      sortOrder.value = order;

      await fetchListSummary();
    };

    const handleSelectAll = (selectedItems: ListSummaryItem[]) => {
      PickupDashboardController.setTransferPickup(selectedItems);
    };

    const onSelectRow = (selectedItems: ListSummaryItem[]) => {
      PickupDashboardController.setTransferPickup(selectedItems);
    };

    const onPageChange = async (newPage: number) => {
      page.value = newPage;
      await fetchListSummary();
    };

    const onHandleModal = (item: ListSummaryItem) => {
      if (item.proofs.length) {
        image.value = item.proofs[0];
        images.value = item.proofs.map((item: any) => {
          return {
            image: item,
            url: item,
          };
        });
        showImagePreview.value = true;
      }
    };

    const closeImagePreview = () => {
      images.value = [];
      showImagePreview.value = false;
    };

    const onBooking = (item: ListSummaryItem) => {
      router.push(`/booking-v2/create/by-shipment-id?bulk=${item.shipmentId}`);
    };

    const fmtQty = (val: number | string) =>
      formatByCountry(Number(val || 0), { type: "quantity" });

    onMounted(async () => {
      await fetchListSummary();
    });

    return {
      columns,
      sortKey,
      sortOrder,
      showImagePreview,
      images,
      image,
      listSummaryData,
      detailSummary,
      pagination,
      startIndex,
      isLoading,
      fmtQty,
      handleSort,
      handleSelectAll,
      onSelectRow,
      onPageChange,
      onHandleModal,
      closeImagePreview,
      onBooking,
      formatPhoneNumber,
      waNumber
    };
  }
});
